import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import {
    AppBar,
    Toolbar,
    Typography,
    Container,
    Button,
    Box,
    CircularProgress,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Divider,
    ThemeProvider,
    createTheme,
    FormControl,
    Select,
    MenuItem,
    InputLabel
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import logo from './assets/logo.png';
import ReportGenerator from './pages/ReportGenerator';
import Testimonials from './pages/Testimonials';
import LinterRules from './pages/LinterRules';
import SharedReport from './pages/SharedReport';
import SharedChat from './pages/SharedChat';
import Chat from './pages/Chat';
import Auth from './components/Auth';
import apiService from './services/api';

// Mobile Navigation Drawer Component
function MobileNavDrawer({ open, onClose, onLogout, years, selectedYear, onYearChange, countries, selectedCountry, onCountryChange }) {
    const navigate = useNavigate();

    const handleNavigation = (path) => {
        navigate(path);
        onClose();
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
        >
            <Box
                sx={{ width: 250 }}
                role="presentation"
            >
                <List>
                    <ListItem>
                        <Typography variant="h6">
                            Navigation
                        </Typography>
                    </ListItem>
                    <Divider />

                    {/* Year Selector in Mobile Menu */}
                    {years && years.length > 1 && (
                        <ListItem>
                            <FormControl fullWidth size="small" sx={{ my: 1 }}>
                                <InputLabel id="mobile-year-select-label">Year</InputLabel>
                                <Select
                                    labelId="mobile-year-select-label"
                                    id="mobile-year-select"
                                    value={selectedYear}
                                    label="Year"
                                    onChange={(e) => onYearChange(e.target.value)}
                                >
                                    {years.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </ListItem>
                    )}

                    {/* Country Selector in Mobile Menu */}
                    {countries && countries.length > 0 && (
                        <ListItem>
                            <FormControl fullWidth size="small" sx={{ my: 1 }}>
                                <InputLabel id="mobile-country-select-label">Country</InputLabel>
                                <Select
                                    labelId="mobile-country-select-label"
                                    id="mobile-country-select"
                                    value={selectedCountry}
                                    label="Country"
                                    onChange={(e) => onCountryChange(e.target.value)}
                                >
                                    {countries.map((country) => (
                                        <MenuItem key={country} value={country}>
                                            {country === 'UK' ? 'United Kingdom' :
                                                country === 'US' ? 'United States' :
                                                    country === 'CANADA' ? 'Canada' : country}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </ListItem>
                    )}
                    <ListItemButton onClick={() => handleNavigation('/report')}>
                        <ListItemText primary="Report" />
                    </ListItemButton>
                    <ListItemButton onClick={() => handleNavigation('/testimonials')}>
                        <ListItemText primary="Testimonials" />
                    </ListItemButton>
                    <ListItemButton onClick={() => handleNavigation('/chat')}>
                        <ListItemText primary="Chat" />
                    </ListItemButton>
                    <ListItemButton onClick={() => handleNavigation('/checks')}>
                        <ListItemText primary="Checks" />
                    </ListItemButton>
                    <Divider />
                    <ListItemButton onClick={() => {
                        onLogout();
                        onClose();
                    }}>
                        <ListItemText primary="Logout" />
                    </ListItemButton>
                </List>
            </Box>
        </Drawer>
    );
}

// Create a theme with the specified primary color
const theme = createTheme({
    palette: {
        primary: {
            main: '#5b1256',
        },
    },
});

function App() {
    // Authentication state
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Mobile menu state
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Force authentication check on component mount
    useEffect(() => {
        console.log('App mounted, checking authentication');
        setIsAuthenticated(apiService.isAuthenticated());
    }, []);

    // Shared state
    const [brands, setBrands] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [brandCategories, setBrandCategories] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [availableYears, setAvailableYears] = useState([2025]);
    const [selectedYear, setSelectedYear] = useState(2025);
    const [availableCountries, setAvailableCountries] = useState(['UK']);
    const [selectedCountry, setSelectedCountry] = useState('UK');
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState('');
    const [isChangingYear, setIsChangingYear] = useState(false);

    // Generated content state
    const [generatedReport, setGeneratedReport] = useState(null);

    // Chat state
    const [chatSessionId, setChatSessionId] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);

    // Fetch available years when authenticated
    useEffect(() => {
        const fetchYears = async () => {
            if (!isAuthenticated) {
                return;
            }

            try {
                const yearsResponse = await apiService.get('/years');
                if (yearsResponse.data && yearsResponse.data.years) {
                    setAvailableYears(yearsResponse.data.years);
                }
            } catch (err) {
                console.error('Error fetching years:', err);
                // Keep default year if fetch fails
            }
        };

        fetchYears();
    }, [isAuthenticated]);

    // Fetch brands and all categories when authenticated or year/country changes
    useEffect(() => {
        const fetchData = async () => {
            if (!isAuthenticated) {
                return; // Don't fetch data if not authenticated
            }

            // Skip data fetching if we're in the middle of changing the year
            // (handleYearChange will fetch the data after updating the country)
            if (isChangingYear) {
                return;
            }

            try {
                setLoadingData(true);
                const [brandsResponse, categoriesResponse, countriesResponse] = await Promise.all([
                    apiService.getBrands(selectedCountry, selectedYear),
                    apiService.getCategories(selectedCountry, selectedYear),
                    apiService.get(`/countries?year=${selectedYear}`)
                ]);

                // Filter out any null/undefined values from the arrays
                const cleanBrands = (brandsResponse.data.brands || []).filter(b => b != null);
                const cleanCategories = (categoriesResponse.data.categories || []).filter(c => c != null);

                setBrands(cleanBrands);
                setAllCategories(cleanCategories);

                // Set available countries if the endpoint returns them
                if (countriesResponse.data && countriesResponse.data.countries) {
                    setAvailableCountries(countriesResponse.data.countries);
                    // If the current selected country is not available, default to UK or the first available country
                    if (!countriesResponse.data.countries.includes(selectedCountry)) {
                        setSelectedCountry(countriesResponse.data.countries.includes('UK') ? 'UK' : countriesResponse.data.countries[0]);
                    }
                }

                setLoadingData(false);
            } catch (err) {
                console.error('Error fetching data:', err);
                setError('Failed to load brands and categories. Please try again later.');
                setLoadingData(false);
            }
        };

        fetchData();
    }, [isAuthenticated, selectedCountry, selectedYear, isChangingYear]); // Re-run when authentication, country, or year changes

    // Fetch categories for the selected brand, country, and year
    useEffect(() => {
        const fetchBrandCategories = async () => {
            if (!isAuthenticated || !selectedBrand) {
                setBrandCategories(allCategories);
                return;
            }

            try {
                setLoadingData(true);
                // Include country and year parameters
                const response = await apiService.getBrandCategories(selectedBrand, selectedCountry, selectedYear);
                setBrandCategories(response.data.categories);

                // If the current category is not in the new list of categories, reset it
                if (selectedCategory && !response.data.categories.includes(selectedCategory)) {
                    setSelectedCategory('');
                }

                setLoadingData(false);
            } catch (err) {
                console.error('Error fetching brand categories:', err);
                setError('Failed to load categories for this brand. Please try again later.');
                setLoadingData(false);
            }
        };

        fetchBrandCategories();
    }, [selectedBrand, selectedCountry, selectedYear, allCategories, selectedCategory, isAuthenticated]);

    // Handler for brand selection
    const handleBrandChange = (brand) => {
        setSelectedBrand(brand);
    };

    // Handler for category selection
    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
    };

    // Handler for year selection
    const handleYearChange = async (year) => {
        if (year === selectedYear) return; // No change needed

        // Set flag to prevent the useEffect from fetching during transition
        setIsChangingYear(true);
        setLoadingData(true);

        // Reset brand and category selection
        setSelectedBrand('');
        setSelectedCategory('');

        try {
            // Fetch available countries for the new year
            const countriesResponse = await apiService.get(`/countries?year=${year}`);

            if (countriesResponse.data && countriesResponse.data.countries) {
                const newCountries = countriesResponse.data.countries;

                // Determine the new country
                let newCountry = selectedCountry;
                if (!newCountries.includes(selectedCountry)) {
                    newCountry = newCountries.includes('UK') ? 'UK' : newCountries[0];
                }

                // Fetch data for the new year and country
                const [brandsResponse, categoriesResponse] = await Promise.all([
                    apiService.getBrands(newCountry, year),
                    apiService.getCategories(newCountry, year)
                ]);

                // Filter out any null/undefined values from the arrays
                const cleanBrands = (brandsResponse.data.brands || []).filter(b => b != null);
                const cleanCategories = (categoriesResponse.data.categories || []).filter(c => c != null);

                // Update all state together
                setAvailableCountries(newCountries);
                setSelectedYear(year);
                setSelectedCountry(newCountry);
                setBrands(cleanBrands);
                setAllCategories(cleanCategories);

                setLoadingData(false);
                setIsChangingYear(false);
                setError('');
            }
        } catch (err) {
            console.error('Error changing year:', err);
            setError('Failed to load data for the selected year. Please try again.');
            setLoadingData(false);
            setIsChangingYear(false);
        }
    };

    // Handler for country selection
    const handleCountryChange = (country) => {
        if (country === selectedCountry) return; // No change needed

        setSelectedCountry(country);
        // When country changes, we need to refresh brands and categories
        if (isAuthenticated) {
            setLoadingData(true); // Set loading state to true

            // Use Promise.all to fetch both brands and categories at once
            Promise.all([
                apiService.getBrands(country, selectedYear),
                apiService.getCategories(country, selectedYear)
            ])
                .then(([brandsResponse, categoriesResponse]) => {
                    // Filter out any null/undefined values from the arrays
                    const newBrands = (brandsResponse.data.brands || []).filter(b => b != null);
                    const newCategories = (categoriesResponse.data.categories || []).filter(c => c != null);

                    setBrands(newBrands);
                    setAllCategories(newCategories);

                    // Reset selected brand if it's not available in the new country
                    if (selectedBrand && !newBrands.includes(selectedBrand)) {
                        setSelectedBrand('');
                    }

                    // Reset selected category if it's not available in the new country
                    if (selectedCategory && !newCategories.includes(selectedCategory)) {
                        setSelectedCategory('');
                    }

                    // Set loading state back to false
                    setLoadingData(false);

                    console.log(`Updated to ${country} data: ${newBrands.length} brands, ${newCategories.length} categories`);
                })
                .catch(err => {
                    console.error(`Error fetching data for country ${country}:`, err);
                    setError(`Failed to load data for ${country}. Please try again.`);
                    setLoadingData(false);
                });
        }
    };

    // Logout function
    const handleLogout = () => {
        apiService.logout();
        setIsAuthenticated(false);
        // Reset chat state on logout
        setChatSessionId(null);
        setChatMessages([]);
    };

    return (
        <ThemeProvider theme={theme}>
            <Routes>
                {/* Public shared routes - no authentication required */}
                <Route path="/share/report/:reportId" element={<SharedReport />} />
                <Route path="/share/chat/:chatId" element={<SharedChat />} />
                
                {/* All other routes require authentication */}
                <Route 
                    path="/*" 
                    element={
                        <div>
                            {!isAuthenticated ? (
                                <Auth onAuthenticated={setIsAuthenticated} />
                            ) : (
                    <div>
                        <AppBar position="static">
                            <Toolbar>
                                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                    <img src={logo} alt="TrustSpark Logo" style={{ height: '32px', marginRight: '16px' }} />

                                    {/* Year Selector */}
                                    {availableYears.length > 1 && (
                                        <Box sx={{ minWidth: 100, mr: 2, display: { xs: 'none', md: 'flex' } }}>
                                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                                <Select
                                                    value={selectedYear}
                                                    onChange={(e) => handleYearChange(e.target.value)}
                                                    displayEmpty
                                                    sx={{
                                                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                        color: 'white',
                                                        '& .MuiSelect-icon': { color: 'white' },
                                                        borderRadius: 1
                                                    }}
                                                >
                                                    {availableYears.map((year) => (
                                                        <MenuItem key={year} value={year}>
                                                            {year}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    )}

                                    {/* Country Selector */}
                                    {availableCountries.length > 0 && (
                                        <Box sx={{ minWidth: 120, mr: 2, display: { xs: 'none', md: 'flex' } }}>
                                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                                <Select
                                                    value={selectedCountry}
                                                    onChange={(e) => handleCountryChange(e.target.value)}
                                                    displayEmpty
                                                    sx={{
                                                        bgcolor: 'rgba(255, 255, 255, 0.15)',
                                                        color: 'white',
                                                        '& .MuiSelect-icon': { color: 'white' },
                                                        borderRadius: 1
                                                    }}
                                                >
                                                    {availableCountries.map((country) => (
                                                        <MenuItem key={country} value={country}>
                                                            {country === 'UK' ? 'United Kingdom' :
                                                                country === 'US' ? 'United States' :
                                                                    country === 'CANADA' ? 'Canada' : country}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    )}
                                </Box>

                                {/* Desktop Navigation */}
                                <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                                    <Button color="inherit" component={Link} to="/report">
                                        Report
                                    </Button>
                                    <Button color="inherit" component={Link} to="/testimonials">
                                        Testimonials
                                    </Button>
                                    <Button color="inherit" component={Link} to="/chat">
                                        Chat
                                    </Button>
                                    <Button color="inherit" component={Link} to="/checks">
                                        Checks
                                    </Button>
                                    <Button color="inherit" onClick={handleLogout}>
                                        Logout
                                    </Button>
                                </Box>

                                {/* Mobile Navigation - Hamburger Icon */}
                                <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                                    <IconButton
                                        size="large"
                                        edge="end"
                                        color="inherit"
                                        aria-label="menu"
                                        onClick={() => setMobileMenuOpen(true)}
                                    >
                                        <MenuIcon />
                                    </IconButton>
                                </Box>
                            </Toolbar>
                        </AppBar>

                        {/* Mobile Navigation Drawer */}
                        <MobileNavDrawer
                            open={mobileMenuOpen}
                            onClose={() => setMobileMenuOpen(false)}
                            onLogout={handleLogout}
                            years={availableYears}
                            selectedYear={selectedYear}
                            onYearChange={handleYearChange}
                            countries={availableCountries}
                            selectedCountry={selectedCountry}
                            onCountryChange={handleCountryChange}
                        />

                        <Container maxWidth="lg" sx={{ mt: 4 }}>
                            {loadingData && brands.length === 0 ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <Routes>
                                    <Route
                                        path="/"
                                        element={
                                            <ReportGenerator
                                                brands={brands}
                                                categories={brandCategories}
                                                brand={selectedBrand}
                                                category={selectedCategory}
                                                onBrandChange={handleBrandChange}
                                                onCategoryChange={handleCategoryChange}
                                                loadingData={loadingData}
                                                error={error}
                                                setError={setError}
                                                year={selectedYear}
                                                country={selectedCountry}
                                                countries={availableCountries}
                                                onCountryChange={handleCountryChange}
                                                report={generatedReport}
                                                setReport={setGeneratedReport}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/report"
                                        element={
                                            <ReportGenerator
                                                brands={brands}
                                                categories={brandCategories}
                                                brand={selectedBrand}
                                                category={selectedCategory}
                                                onBrandChange={handleBrandChange}
                                                onCategoryChange={handleCategoryChange}
                                                loadingData={loadingData}
                                                error={error}
                                                setError={setError}
                                                year={selectedYear}
                                                country={selectedCountry}
                                                countries={availableCountries}
                                                onCountryChange={handleCountryChange}
                                                report={generatedReport}
                                                setReport={setGeneratedReport}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/report-generator/:reportId"
                                        element={
                                            <ReportGenerator
                                                brands={brands}
                                                categories={brandCategories}
                                                brand={selectedBrand}
                                                category={selectedCategory}
                                                onBrandChange={handleBrandChange}
                                                onCategoryChange={handleCategoryChange}
                                                loadingData={loadingData}
                                                error={error}
                                                setError={setError}
                                                year={selectedYear}
                                                country={selectedCountry}
                                                countries={availableCountries}
                                                onCountryChange={handleCountryChange}
                                                report={generatedReport}
                                                setReport={setGeneratedReport}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/report-generator"
                                        element={
                                            <ReportGenerator
                                                brands={brands}
                                                categories={brandCategories}
                                                brand={selectedBrand}
                                                category={selectedCategory}
                                                onBrandChange={handleBrandChange}
                                                onCategoryChange={handleCategoryChange}
                                                loadingData={loadingData}
                                                error={error}
                                                setError={setError}
                                                year={selectedYear}
                                                country={selectedCountry}
                                                countries={availableCountries}
                                                onCountryChange={handleCountryChange}
                                                report={generatedReport}
                                                setReport={setGeneratedReport}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/testimonials"
                                        element={
                                            <Testimonials
                                                brands={brands}
                                                categories={brandCategories}
                                                brand={selectedBrand}
                                                category={selectedCategory}
                                                onBrandChange={handleBrandChange}
                                                onCategoryChange={handleCategoryChange}
                                                loadingData={loadingData}
                                                error={error}
                                                setError={setError}
                                                year={selectedYear}
                                                country={selectedCountry}
                                            />
                                        }
                                    />
                                    <Route
                                        path="/chat"
                                        element={
                                            <Chat
                                                sessionId={chatSessionId}
                                                setSessionId={setChatSessionId}
                                                messages={chatMessages}
                                                setMessages={setChatMessages}
                                                selectedCountry={selectedCountry}
                                                selectedYear={selectedYear}
                                            />
                                        }
                                    />
                                    <Route path="/checks" element={<LinterRules />} />
                                    <Route
                                        path="/chat/:chatId"
                                        element={
                                            <Chat
                                                sessionId={chatSessionId}
                                                setSessionId={setChatSessionId}
                                                messages={chatMessages}
                                                setMessages={setChatMessages}
                                                selectedCountry={selectedCountry}
                                                selectedYear={selectedYear}
                                            />
                                        }
                                    />
                                </Routes>
                            )}
                        </Container>

                        <Box component="footer" sx={{ bgcolor: '#f5f5f5', py: 3, mt: 4 }}>
                            <Container maxWidth="lg">
                                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                                    To learn more about the BrandSpark Most Trusted Awards program, methodology, or for a full list of winning brands, visit{' '}
                                    <a href="https://www.brandsparkmosttrusted.com" target="_blank" rel="noopener noreferrer">
                                        www.brandsparkmosttrusted.com
                                    </a>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                                    Contact: Robert Levy, BrandSpark{' '}
                                    <a href="mailto:RLevy@BrandSpark.com">RLevy@BrandSpark.com</a>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center">
                                    © 2025 BrandSpark International Inc. All Rights Reserved
                                </Typography>
                            </Container>
                        </Box>
                    </div>
                )}
            </div>
                    }
                />
            </Routes>
        </ThemeProvider>
    );
}

export default App;
